import React from "react";
import {
  Autocomplete,
  Box,
  Chip,
  CircularProgress,
  styled,
} from "@mui/material";
import CheckBoxOutlineBlankIcon from "@mui/icons-material/CheckBoxOutlineBlank";
import CheckBoxIcon from "@mui/icons-material/CheckBox";
import { SelectOption } from "@/types";
import { CustomTextInput } from "./TextInput";
import { CustomCheckbox } from "./Checkbox";
import { generateColorFromText } from "@/lib/utils/colorUtils";
import { PRIMARY_COLORS, PRIMARY_SILVER_COLOR } from "..";
import { TagsHierarchyV2 } from "@/types/tagsV2";

const icon = <CheckBoxOutlineBlankIcon fontSize="small" />;
const checkedIcon = <CheckBoxIcon fontSize="small" />;

const StyledBadgeChip = styled(Chip)<{
  chipColor?: string;
  chipBackground?: string;
}>(
  ({ chipColor = "white", chipBackground = PRIMARY_COLORS.PRIMARY_GREEN }) => ({
    backgroundColor: chipBackground,
    color: chipColor,
    "& .MuiChip-deleteIcon": {
      color: chipColor,
      opacity: 0.75,
      "&:hover": {
        color: chipColor,
        opacity: 1,
      },
    },
  })
);

export interface MultiSelectAutocompleteProps<
  V extends string | number = string,
> {
  options: SelectOption<V>[];
  selected: Array<V>;
  setSelected: (selected: Array<V>) => void;
  label?: string;
  placeholder?: string;
  error?: boolean;
  helperText?: string;
  getBadgeProps?: (option: SelectOption<V>) => {
    color?: string;
    background?: string;
  };
  customBadgeColors?: {
    color?: string;
    background?: string;
  };
  maxHeight?: number | string;
  size?: "small" | "medium";
  allowChipDelete?: boolean;
  showSelectAll?: boolean;
  loading?: boolean;
  markets?: TagsHierarchyV2[];
  hasLoader?: boolean;
  disabled?: boolean;
  renderOption?: (
    props: React.HTMLAttributes<HTMLLIElement>,
    option: { value: V; label: string },
    { selected }: { selected: boolean }
  ) => React.ReactNode;
  slotDropdownProps?: {
    listbox?: {
      sx?: React.CSSProperties;
    };
  };
}

export function MultiSelectAutocomplete<V extends string | number = string>({
  options,
  selected,
  setSelected,
  label = "Select options",
  placeholder = "Search and select...",
  error = false,
  helperText,
  getBadgeProps,
  size = "small",
  allowChipDelete = true,
  showSelectAll = true,
  loading = false,
  disabled,
  customBadgeColors,
  renderOption,
  slotDropdownProps,
}: MultiSelectAutocompleteProps<V>) {
  const selectedOptions = options.filter((option) =>
    selected.includes(option.value)
  );

  const handleRemoveOption = (optionValue: V) => {
    setSelected(selected.filter((value) => value !== optionValue));
  };

  const handleSelectAll = () => {
    const allSelected = selected.length === options.length;
    const newSelected = allSelected
      ? []
      : options.map((option) => option.value);
    setSelected(newSelected);
  };

  const isAllSelected = selected.length === options.length;
  const isIndeterminate = selected.length > 0 && !isAllSelected;

  // Add "Select All" option to the beginning of options if enabled
  const optionsWithSelectAll = showSelectAll
    ? [{ value: "__select_all__" as V, label: "Select All" }, ...options]
    : options;

  const defaultRenderOption = (
    props: React.HTMLAttributes<HTMLLIElement>,
    option: SelectOption<V>,
    { selected }: { selected: boolean }
  ) => {
    const { ...optionProps } = props;

    if (showSelectAll && option.value === "__select_all__") {
      return (
        <li
          key={option.value}
          {...optionProps}
          onClick={(e: React.MouseEvent) => {
            e.preventDefault();
            handleSelectAll();
          }}
          style={{
            ...optionProps.style,
            borderBottom: `1px solid ${PRIMARY_SILVER_COLOR[250]}`,
          }}
        >
          <CustomCheckbox
            icon={icon}
            checkedIcon={checkedIcon}
            style={{ marginRight: 8 }}
            checked={isAllSelected}
            indeterminate={isIndeterminate}
            isParent={true}
          />
          Select All
        </li>
      );
    }

    return (
      <li key={option.value} {...optionProps}>
        <CustomCheckbox
          icon={icon}
          checkedIcon={checkedIcon}
          style={{ marginRight: 8 }}
          checked={selected}
        />
        {option.label}
      </li>
    );
  };

  const fieldPlaceholder = loading
    ? "Loading..."
    : !selectedOptions.length
      ? placeholder
      : "";

  return (
    <Autocomplete
      multiple
      loading={loading}
      options={optionsWithSelectAll}
      disableCloseOnSelect
      getOptionLabel={(option) => option.label.toString()}
      value={selectedOptions}
      disabled={loading || disabled}
      onChange={(_event, newValue) => {
        // Filter out the "Select All" option from selected values
        const filteredValues = newValue.filter(
          (option) => option.value !== "__select_all__"
        );
        const newSelected = filteredValues.map((option) => option.value);
        setSelected(newSelected);
      }}
      slotProps={slotDropdownProps}
      renderOption={renderOption || defaultRenderOption}
      renderValue={(value) => (
        <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
          {value.map((option) => {
            const badgeProps =
              getBadgeProps?.(option) ||
              generateColorFromText(option.label.toString());

            return (
              <StyledBadgeChip
                key={option.value}
                label={option.label}
                chipColor={customBadgeColors?.color || badgeProps.color}
                chipBackground={
                  customBadgeColors?.background || badgeProps.background
                }
                onDelete={
                  allowChipDelete
                    ? () => handleRemoveOption(option.value)
                    : undefined
                }
                size={size}
              />
            );
          })}
        </Box>
      )}
      renderInput={(params) => (
        <CustomTextInput
          {...params}
          label={label}
          sx={{
            "& input::placeholder": {
              fontSize: "20px",
            },
          }}
          placeholder={fieldPlaceholder}
          error={error}
          helperText={helperText}
          size={size}
          disabled={loading || disabled}
          slotProps={{
            input: {
              ...params.InputProps,
              startAdornment: (
                <>
                  {loading && <CircularProgress size={15} />}
                  {params.InputProps.startAdornment}
                </>
              ),
            },
          }}
        />
      )}
    />
  );
}
